import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React from "react";
import QRCode from "react-qr-code";

export const ComponentToPrint = React.forwardRef<HTMLDivElement, { code: string; url: string }>(
	({ code, url }, ref) => {
		const { format } = useI18n();
		const steps = [
			{ id: "step_1", label: format("home.pendingPatientDialog.print.step_1") },
			{ id: "step_2", label: format("home.pendingPatientDialog.print.step_2") },
			{ id: "step_3", label: format("home.pendingPatientDialog.print.step_3") },
			{ id: "step_4", label: format("home.pendingPatientDialog.print.step_4") },
		];
		return (
			<Container ref={ref}>
				<Header>
					<H3>{format("home.pendingPatientDialog.print.title")}</H3>
					<div className="p-size-s-rubik-regular">{format("home.pendingPatientDialog.print.subtitle")}</div>
				</Header>
				<Description>
					<div>{format("home.pendingPatientDialog.print.fisrtDescription")}</div>
					<div>{format("home.pendingPatientDialog.print.secondDescription")}</div>
				</Description>
				<Content>
					<div style={{ width: 170, height: 170 }}>
						<StyledQRCode value={url} size={170} />
					</div>
					<Steps>
						{steps.map((step, index) => (
							<Step key={step.id}>
								<StepIndex className="p-size-s-bold">{index + 1}.</StepIndex>
								<span className="p-size-xs-semi-bold">{step.label}</span>
							</Step>
						))}
					</Steps>
				</Content>
				<CodeContainer>
					<div className="p-size-s-bold">{format("home.pendingPatientDialog.print.code.description_1")}</div>
					<ColoredText className="p-size-s-bold">
						{format("home.pendingPatientDialog.print.code.description_2")}
					</ColoredText>
					<H3 style={{ paddingTop: "16px" }}>{code}</H3>
				</CodeContainer>
				<div style={{ textAlign: "center" }}>
					{format("home.pendingPatientDialog.print.footer", { color: colors.primary })}
				</div>
			</Container>
		);
	},
);

ComponentToPrint.displayName = "ComponentToPrint";

const Container = styled.div`
	padding: 80px;
	${stack("L", "flexstart", "stretch")};
	flex: 1;
	background-color: ${colors.white};
	position: relative;

	@media print {
		background-color: ${colors.white} !important;
		-webkit-print-color-adjust: exact;
	}

	:after {
		position: absolute;
		content: "";
		bottom: 80px;
		width: calc(100% - 160px);
		height: 1px;
		border-bottom: 1px solid ${colors.primary25};
	}
`;

const Header = styled.div`
	${stack(undefined, "center", "center")};
	padding-bottom: 16px;

	border-bottom: 1px solid ${colors.primary25};
`;

const H3 = styled.h3`
	color: ${colors.primary};
`;

const Description = styled.div`
	${stack()};
`;

const Content = styled.div`
	${row(18)}
`;

const Steps = styled.div`
	${stack(16)};
`;

const Step = styled.div`
	padding-left: 1.5rem;
	position: relative;

	span {
		color: ${colors.black};
	}
`;
const StepIndex = styled.div`
	position: absolute;
	top: 0;
	left: 0;
`;

const CodeContainer = styled.div`
	position: relative;
	padding: 24px;
	${stack(undefined, "center", "center")};
	border-radius: 8px;
	border: 2px solid ${colors.primaryBackground};
	background-color: ${colors.primaryBackground};

	@media print {
		background-color: ${colors.primaryBackground} !important;
		-webkit-print-color-adjust: exact;
	}
`;

const ColoredText = styled.span`
	color: ${colors.primary};
`;

const StyledQRCode = styled(QRCode)`
	print-color-adjust: exact;
`;
